import React, { useState } from "react";
import { supabase } from "../../lib/supabase";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function InstallBanner() {
  const [show, setShow] = React.useState(false);
  const [isIOS, setIsIOS] = React.useState(false);
  const [installPrompt, setInstallPrompt] = React.useState<BeforeInstallPromptEvent | null>(null);

  React.useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (isStandalone || dismissed) return;

    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Chrome/Android: listen for native install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setShow(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS / other mobile: show manual instructions
    if (isMobile) {
      setIsIOS(ios);
      setShow(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  function dismiss() {
    localStorage.setItem('pwa-banner-dismissed', '1');
    setShow(false);
  }

  async function handleInstall() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') dismiss();
  }

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: '#1a1a1a', color: 'white', padding: '14px 20px',
      display: 'flex', alignItems: 'flex-start', gap: 12,
      borderTop: '1px solid #333'
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10, background: '#C47A2F',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, fontWeight: 800, color: 'white', flexShrink: 0
      }}>AP</div>
      <div style={{ flex: 1 }}>
        {installPrompt ? (
          <>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>📱 Install AusProp on your device</div>
            <button
              onClick={handleInstall}
              style={{
                background: '#C47A2F', color: 'white', border: 'none', borderRadius: 8,
                padding: '6px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Install app
            </button>
          </>
        ) : isIOS ? (
          <>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Add to your home screen</div>
            <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>
              💡 On iPhone: tap the <strong style={{ color: '#C47A2F' }}>Share</strong> button then <strong style={{ color: '#C47A2F' }}>'Add to Home Screen'</strong>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>Quick access</div>
            <div style={{ fontSize: 12, color: '#999', lineHeight: 1.5 }}>
              💡 Add <strong style={{ color: '#C47A2F' }}>ausprop.app</strong> to your home screen or bookmark it for quick access
            </div>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        style={{ background: 'none', border: 'none', color: '#666', fontSize: 20, cursor: 'pointer', padding: '0 4px', flexShrink: 0 }}
      >×</button>
    </div>
  );
}

type Tab = "signin" | "signup";
type View = "auth" | "forgot";

export default function AuthPage() {
  const [tab, setTab] = useState<Tab>("signin");
  const [view, setView] = useState<View>("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  function reset() {
    setError(null);
    setSuccess(null);
  }

  function switchTab(t: Tab) {
    setTab(t);
    setPassword("");
    setConfirmPassword("");
    reset();
  }

  function openForgot() {
    reset();
    setPassword("");
    setView("forgot");
  }

  function backToSignIn() {
    reset();
    setView("auth");
    setTab("signin");
  }

  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    reset();
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) setError(err.message);
  }

  async function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    if (!email.trim()) { setError("Enter your email address first."); return; }
    reset();
    setLoading(true);
    const { error: err } = await supabase.auth.signInWithOtp({ email });
    setLoading(false);
    if (err) setError(err.message);
    else setSuccess("Magic link sent — check your email.");
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    reset();
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true);
    const { error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/` },
    });
    setLoading(false);
    if (err) setError(err.message);
    else setSuccess("Check your email to confirm your account.");
  }

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault();
    if (!supabase) return;
    if (!email.trim()) { setError("Enter your email address."); return; }
    reset();
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: "https://ausprop.app",
    });
    setLoading(false);
    if (err) setError(err.message);
    else setSuccess(`Check your email — we've sent a password reset link to ${email}`);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#f7f7f4]">
      <InstallBanner />
      <div className="w-full max-w-sm">
        {/* Wordmark */}
        <div className="mb-8 text-center">
          <span className="text-2xl font-semibold tracking-tight text-[#C47A2F]">
            AusProp
          </span>
          <p className="mt-1 text-sm text-neutral-500">Australian Property Investor Platform</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-lg">

          {/* ── Forgot password view ── */}
          {view === "forgot" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-neutral-900">Reset password</h2>
                <p className="mt-1 text-sm text-neutral-500">
                  Enter your email and we'll send you a reset link.
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-neutral-700 mb-1">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                  />
                </div>

                {error && <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
                {success && <p className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">{success}</p>}

                {!success && (
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-neutral-900 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? "Sending…" : "Send reset link"}
                  </button>
                )}
              </form>

              <button
                type="button"
                onClick={backToSignIn}
                className="text-xs text-neutral-400 hover:text-neutral-700 transition-colors"
              >
                ← Back to sign in
              </button>
            </div>
          )}

          {/* ── Normal auth view ── */}
          {view === "auth" && (
            <>
              {/* Tabs */}
              <div className="mb-6 flex rounded-xl border border-neutral-200 p-1 gap-1">
                {(["signin", "signup"] as Tab[]).map((t) => (
                  <button
                    key={t}
                    onClick={() => switchTab(t)}
                    className={`flex-1 rounded-lg py-1.5 text-sm font-medium transition-colors ${
                      tab === t
                        ? "bg-neutral-900 text-white"
                        : "text-neutral-500 hover:text-neutral-800"
                    }`}
                  >
                    {t === "signin" ? "Sign in" : "Sign up"}
                  </button>
                ))}
              </div>

              {/* Sign in */}
              {tab === "signin" && (
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Password</label>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>

                  {error && <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
                  {success && <p className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">{success}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-neutral-900 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? "Signing in…" : "Sign in"}
                  </button>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={openForgot}
                      className="text-xs text-neutral-400 hover:text-neutral-700 transition-colors"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <div className="relative flex items-center gap-2 py-1">
                    <div className="flex-1 border-t border-neutral-200" />
                    <span className="text-xs text-neutral-400">or</span>
                    <div className="flex-1 border-t border-neutral-200" />
                  </div>

                  <button
                    type="button"
                    onClick={handleMagicLink}
                    disabled={loading}
                    className="w-full rounded-lg border border-neutral-200 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 transition-colors"
                  >
                    {loading ? "Sending…" : "Send magic link"}
                  </button>
                </form>
              )}

              {/* Sign up */}
              {tab === "signup" && (
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Email</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Password</label>
                    <input
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-700 mb-1">Confirm password</label>
                    <input
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>

                  {error && <p className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</p>}
                  {success && <p className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-xs text-green-700">{success}</p>}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-neutral-900 py-2 text-sm font-medium text-white hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                  >
                    {loading ? "Creating account…" : "Create account"}
                  </button>
                </form>
              )}
            </>
          )}

          <p style={{
            textAlign: 'center',
            fontSize: 12,
            color: '#9CA3AF',
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #F3F4F6'
          }}>
            💡 Add <strong>ausprop.app</strong> to your home screen or bookmark it for quick access
          </p>
        </div>

        <div className="mt-4 space-y-1 text-center">
          <p className="text-xs text-neutral-400">Your portfolio data is encrypted and stored securely.</p>
          <p className="text-xs text-neutral-400">Hosted on Supabase — SOC 2 compliant, encrypted at rest. Your data is never sold or shared.</p>
          <p className="text-xs text-neutral-400">Questions? <a href="mailto:propertyoshq@gmail.com" className="underline hover:text-neutral-600">propertyoshq@gmail.com</a></p>
          <p className="text-xs text-neutral-400">
            <a href="/terms" className="underline hover:text-neutral-600">Terms of Service</a>
            {" · "}
            <a href="/privacy" className="underline hover:text-neutral-600">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}
